import React, { Fragment } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  HeaderGroup,
} from "@tanstack/react-table";

interface ReusableSimpleTableProps {
  columns: any[];
  data: any[];
  renderDetailPanel?: ({ row }: { row: any }) => React.ReactElement;
  expandedRowId?: string | number | null;
  setExpandedRowId?: (id: string | number | null) => void;
}

const ReusableSimpleTable: React.FC<ReusableSimpleTableProps> = ({
  columns,
  data,
  renderDetailPanel,
  expandedRowId,
  setExpandedRowId,
}) => {
  const table = useReactTable({
    data,
    columns,
    getRowCanExpand: () => !!renderDetailPanel,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    debugTable: false,
  });

  return (
    <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
      <Table>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup: HeaderGroup<any>) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell
                  key={header.id}
                  {...header.column.columnDef.meta}
                  onClick={header.column.getToggleSortingHandler?.()}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <Fragment key={row.id}>
              <TableRow
                onClick={() => {
                  if (setExpandedRowId) {
                    setExpandedRowId(
                      String(expandedRowId) === String(row.original.id)
                        ? null
                        : row.original.id
                    );
                  }
                }}
                style={{ cursor: renderDetailPanel ? "pointer" : undefined }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} {...cell.column.columnDef.meta}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
              {String(expandedRowId) === String(row.original.id) &&
                renderDetailPanel && (
                  <TableRow>
                    <TableCell colSpan={row.getVisibleCells().length}>
                      {renderDetailPanel({ row })}
                    </TableCell>
                  </TableRow>
                )}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ReusableSimpleTable;
